<template>
  <div class="suggested-locations-page">
    <h1 class="global-h1 suggested-locations-page__title">Set up your local promotion</h1>
    <p class="global-p suggested-locations-page__subtitle">Let's turn the places and experiences you love into your first social posts.</p>

    <OnboardingStepsHeader class="suggested-locations-page__steps" :currentStep="1" />

    <div v-if="isLoading" class="suggested-locations-page__loader">
      <LoaderNew style="width: 70px; height: 70px" :borderWidth="3" />
    </div>

    <template v-else>
      <div class="suggested-locations-page__section-heading">
        <div>
          <h2 class="global-h3 suggested-locations-page__section-title">Suggested locations &amp; events</h2>
          <p class="suggested-locations-page__section-subtitle">We found these locations and events to get you started. Add or adjust to make the list yours.</p>
        </div>
      </div>

      <NoteWithIcon v-if="loadError" class="suggested-locations-page__note" color="red" size="small">
        <template #text>
          <div>{{ loadError }}</div>
        </template>
      </NoteWithIcon>

      <NoteWithIcon v-if="isDiscoveryRunning" class="suggested-locations-page__note" color="gray" size="small">
        <template #text>
          <div>We're still finding local highlights for you. This list updates on its own.</div>
        </template>
      </NoteWithIcon>

      <NoteWithIcon v-if="importFailures.length" class="suggested-locations-page__note" color="yellow" size="small">
        <template #text>
          <div>
            {{ importFailures.length === 1 ? "One suggestion couldn't be added:" : `${importFailures.length} suggestions couldn't be added:` }}
            <ul class="suggested-locations-page__failures">
              <li v-for="failure in importFailures" :key="failure.suggestionId">
                <strong>{{ failure.name }}</strong
                >{{ failure.message ? `: ${failure.message}` : "" }}
              </li>
            </ul>
            Remove them or fix them with Edit, then continue.
          </div>
        </template>
      </NoteWithIcon>

      <template v-if="activeItems.length || additionalItems.length">
        <div class="suggested-locations-page__filters">
          <FilterChips v-if="typeChips.length > 2" v-model="selectedType" :chips="typeChips" ariaLabel="Filter by type" />

          <!-- Cities only help when there is more than one to choose from. -->
          <FilterChips v-if="cities.length > 1" v-model="selectedCity" :chips="cityChips" ariaLabel="Filter by city" />
        </div>

        <div class="suggested-locations-page__summary">
          <span>
            {{ summaryText }}
            <template v-if="addedItems.length">
              · {{ addedItems.length }} added
              <LinkWithIcon isDottedUnderline color="gray" @click="isShowingAdded = !isShowingAdded">{{ isShowingAdded ? "hide" : "show" }}</LinkWithIcon>
            </template>
          </span>
          <div class="suggested-locations-page__search" :class="{ 'suggested-locations-page__search--open': isSearchOpen }">
            <input
              v-show="isSearchOpen"
              ref="searchInputRef"
              v-model="search"
              class="global-text-input"
              type="text"
              aria-label="Search places and events"
              placeholder="Search places and events"
              @keydown.escape="closeSearch"
              @blur="!search.trim() && (isSearchOpen = false)"
            />
            <button type="button" class="suggested-locations-page__search-toggle" :aria-label="isSearchOpen ? 'Close search' : 'Search places and events'" @click="toggleSearch">
              <IconEmbedded name="magnifier_2-5" :size="17" color="#667085" />
            </button>
            <SrpButton color="gray" fill="outlined" size="small" :isDisabled="isBusy" @click="openLocationEditor(null)">
              <template #icon><IconEmbedded name="plus_2-5" :size="16" /></template>
              Add your own
            </SrpButton>
          </div>
        </div>

        <p v-if="!groups.length" class="suggested-locations-page__empty">Nothing matches these filters.</p>

        <template v-if="groups.length">
          <div v-for="group in groups" :key="group.option.value" class="suggested-locations-page__group">
            <div class="suggested-locations-page__group-header">
              <h3 class="suggested-locations-page__group-title">{{ categoryLabel(group.option) }}</h3>
              <LocationCategoryIcon :option="group.option" :size="30" />
              <span class="suggested-locations-page__group-count">{{ group.includedCount }} included</span>
            </div>
            <SuggestedLocationRow v-for="item in group.items" :key="item.key" :item="item" :isDisabled="isBusy" :showDebug="showDebug" @edit="editItem(item)" @remove="setReviewStatus(item, 'Rejected')" />
            <div v-if="group.additional.length" class="suggested-locations-page__additional">
              <div class="suggested-locations-page__additional-heading">
                <button
                    type="button"
                    class="suggested-locations-page__more-toggle"
                    :aria-label="additionalLabel(group.option, group.additional.length)"
                    :aria-expanded="Boolean(expandedCategories[group.option.value])"
                    :aria-controls="`additional-${group.option.value}`"
                    @click="expandedCategories[group.option.value] = !expandedCategories[group.option.value]"
                  >
                    <span class="suggested-locations-page__additional-label">
                      <span class="suggested-locations-page__chevron" :class="{ 'suggested-locations-page__chevron--expanded': expandedCategories[group.option.value] }" aria-hidden="true"></span>
                      <span>{{ additionalLabel(group.option, group.additional.length) }}</span>
                    </span>
                    <span class="suggested-locations-page__additional-hint"><strong>Not included yet.</strong> Include any you'd like to promote.</span>
                </button>
                <SrpButton
                  tag="button"
                  type="button"
                  :color="expandedCategories[group.option.value] ? 'teal' : 'gray'"
                  fill="outlined"
                  size="small"
                  class="suggested-locations-page__include-all"
                  :isDisabled="isBusy || isDiscoveryRunning"
                  :disabled="isBusy || isDiscoveryRunning"
                  :aria-label="`${expandedCategories[group.option.value] ? 'Include all' : 'Show all'} ${group.additional.length} ${categoryLabel(group.option)} suggestions`"
                  @click="expandedCategories[group.option.value] ? addAdditional(group.additional) : (expandedCategories[group.option.value] = true)"
                >{{ expandedCategories[group.option.value] ? "Include All" : "Show all" }}</SrpButton>
              </div>
              <div v-if="expandedCategories[group.option.value]" :id="`additional-${group.option.value}`" class="suggested-locations-page__additional-rows">
                <SuggestedLocationRow
                  v-for="item in group.additional"
                  :key="item.key"
                  :item="item"
                  :isDisabled="isBusy || isDiscoveryRunning"
                  :showDebug="showDebug"
                  @add="addAdditional([item])"
                  @remove="setReviewStatus(item, 'Rejected')"
                />
              </div>
            </div>
          </div>
        </template>
      </template>

      <p v-else-if="!isDiscoveryRunning" class="suggested-locations-page__empty">We don't have suggestions for you yet. Add your own local favorites to get started.</p>

      <div v-if="removedItems.length" class="suggested-locations-page__removed">
        <LinkWithIcon isDottedUnderline color="gray" @click="isShowingRemoved = !isShowingRemoved">
          {{ isShowingRemoved ? "Hide removed" : `Show ${removedItems.length} removed` }}
        </LinkWithIcon>
        <div v-if="isShowingRemoved" class="suggested-locations-page__group suggested-locations-page__group--removed">
          <SuggestedLocationRow v-for="item in removedItems" :key="item.key" :item="item" :isDisabled="isBusy" :showDebug="showDebug" @restore="setReviewStatus(item, 'Pending')" />
        </div>
      </div>

      <div ref="footerRef" class="suggested-locations-page__footer" :class="{ 'suggested-locations-page__footer--stuck': isFooterStuck }">
        <div class="suggested-locations-page__footer-status">
          <div class="suggested-locations-page__footer-count">{{ footerCountText }}</div>
          <div class="suggested-locations-page__footer-hint">You can adjust anytime</div>
        </div>
        <SrpButton color="gray" fill="outlined" size="small" :isDisabled="isBusy" @click="openLocationEditor(null)">
          <template #icon><IconEmbedded name="plus_2-5" :size="16" /></template>
          Add your own
        </SrpButton>
        <div class="suggested-locations-page__footer-next">Next: <strong>Upload photos</strong></div>
        <SrpButton color="teal" :isDisabled="isBusy || isDiscoveryRunning" @click="acceptAll">
          <template v-if="isAccepting" #icon><LoadingIcon :size="18" /></template>
          {{ suggestedItems.length ? "Looks Good" : "Continue" }}
          <template #iconRight><IconEmbedded name="arrow-right_2-5" :size="18" /></template>
        </SrpButton>
      </div>
    </template>

    <div style="margin-top: 50px; display: flex; justify-content: flex-end; width: 100%">
      <SuperUserActionPanel>
        <div style="display: flex; flex-wrap: wrap; gap: 8px">
          <SrpButton color="gray" fill="outlined" size="tiny" :to="{ name: 'GoogleNearbyDebug', query: { customerId } }">Nearby Locations (super)</SrpButton>
          <SrpButton tag="button" type="button" color="gray" fill="outlined" size="tiny" :aria-pressed="isShowingDebug" @click="isShowingDebug = !isShowingDebug">{{ isShowingDebug ? "Hide debug" : "Debug" }}</SrpButton>
        </div>
      </SuperUserActionPanel>
    </div>

    <NearbySuggestionEditModal v-model:isVisible="isSuggestionEditorVisible" :customerId="customerId" :suggestion="suggestionToEdit" @saved="replaceSuggestion" />
    <LocationEditorModal v-model:isVisible="isLocationEditorVisible" :customerId="customerId" :location="locationToEdit" @saved="loadSavedLocations" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useHead } from "@unhead/vue";
import { MetricSender } from "@/helpers/MetricSender";
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";
import AdminContext from "@logic/AdminContext";
import { getCalendarId } from "@logic/calendarManager";
import { useUserProfileStore } from "@stores/userProfileStore";
import { EVENT_CATEGORY_OPTION, LOCATION_CATEGORY_OPTIONS, categoryOptionFor } from "@logic/LocationCategories";
import { locationTextMatches } from "@logic/LocationSearch";
import { addSuggestionsToReview, failedImports, importSuggestions, isAdditional, isInReviewList, nearbyErrorMessage, previewLocation } from "@logic/NearbySuggestions";
import NearbyLocationsRepo from "@repos/NearbyLocationsRepo";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import LocationCategoryIcon from "@components/SocialLocations/LocationCategoryIcon.vue";
import LocationEditorModal from "@components/SocialLocations/LocationEditorModal.vue";
import NearbySuggestionEditModal from "@components/NearbyLocations/NearbySuggestionEditModal.vue";
import FilterChips from "./FilterChips.vue";
import OnboardingStepsHeader from "./OnboardingStepsHeader.vue";
import SuggestedLocationRow from "./SuggestedLocationRow.vue";

// Types
import type { FilterChip } from "./FilterChips.vue";
import type { SuggestedLocationItem } from "./SuggestedLocationRow.vue";
import type { LocationCategoryOption } from "@logic/LocationCategories";
import type { LocationData } from "@contracts/socialLocations";
import type { NearbyReviewStatus, NearbyRun, NearbySuggestion } from "@contracts/nearbyLocations";

defineOptions({
  name: "AtlasOnboardingLocations",
});

const route = useRoute();
const router = useRouter();

// Normalize route params ------------------------------------------------------
const customerId = computed<string>(() => (Array.isArray(route.params.customerId) ? route.params.customerId[0] : (route.params.customerId as string)));

const pageTitle = ref("Local Highlights");
useHead({ title: () => pageTitle.value });

// State ======================================================================
const suggestions = ref<NearbySuggestion[]>([]);
const runs = ref<NearbyRun[]>([]);
const savedLocations = ref<LocationData[]>([]);
const isLoading = ref<boolean>(true);
const isBusy = ref<boolean>(false);
const isAccepting = ref<boolean>(false);
const isShowingDebug = ref<boolean>(false);
const userProfileStore = useUserProfileStore();
const showDebug = computed(() => isShowingDebug.value && userProfileStore.isSuperOrSalesUser);
const loadError = ref<string>("");
const importFailures = ref<{ suggestionId: string; name: string; message?: string }[]>([]);
let pollTimer: ReturnType<typeof setTimeout> | undefined;
let isUnmounted = false;

const isDiscoveryRunning = computed<boolean>(() => runs.value.some(run => run.status === "Queued" || run.status === "Running"));

// Load =======================================================================
async function loadSuggestions() {
  clearTimeout(pollTimer);
  try {
    const [newSuggestions, newRuns] = await Promise.all([NearbyLocationsRepo.list(customerId.value), NearbyLocationsRepo.runs(customerId.value)]);
    if (isUnmounted) return;
    suggestions.value = newSuggestions;
    runs.value = newRuns;
    loadError.value = "";
    // Discovery runs in the background; keep refreshing until it settles.
    if (isDiscoveryRunning.value) pollTimer = setTimeout(() => void loadSuggestions(), 5000);
  } catch (error) {
    getGlobalRemoteLogger()?.error(`SuggestedLocationsPage.loadSuggestions failed. customerId=${customerId.value} error=${error}`);
    loadError.value = "We couldn't load your suggestions. Please refresh the page.";
  }
}

async function loadSavedLocations() {
  try {
    savedLocations.value = await SocialLocationsRepo.list(customerId.value, false, false);
  } catch (error) {
    // Non-blocking: the suggestions still work without the already-added rows.
    getGlobalRemoteLogger()?.error(`SuggestedLocationsPage.loadSavedLocations failed. customerId=${customerId.value} error=${error}`);
  }
}

onMounted(async () => {
  const org = await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  if (org?.name) pageTitle.value = `Local Highlights - ${org.name}`;
  MetricSender.sendMetric("Cust-AtlasOnboarding-Locations-PageView", { customerId: customerId.value });
  await Promise.all([loadSuggestions(), loadSavedLocations()]);
  isLoading.value = false;
});

onBeforeUnmount(() => {
  isUnmounted = true;
  clearTimeout(pollTimer);
  footerObserver?.disconnect();
});

// Sticky footer ==============================================================
// The footer looks different pinned to the window edge (flush, open bottom) than resting at the end of the page
// (a closed card). CSS can't tell those apart, so watch for it: with the root shrunk by 1px at the bottom, the
// footer is only fully "visible" when it is NOT touching the window's bottom edge.
const footerRef = ref<HTMLElement | null>(null);
const isFooterStuck = ref<boolean>(false);
let footerObserver: IntersectionObserver | undefined;

watch(footerRef, element => {
  footerObserver?.disconnect();
  if (!element || typeof IntersectionObserver === "undefined") return;
  footerObserver = new IntersectionObserver(([entry]) => (isFooterStuck.value = entry.intersectionRatio < 1), { threshold: [1], rootMargin: "0px 0px -1px 0px" });
  footerObserver.observe(element);
});

// Items ======================================================================
const OTHER_OPTION = LOCATION_CATEGORY_OPTIONS[LOCATION_CATEGORY_OPTIONS.length - 1];
/** Group and chip order: the shared category order, events last. */
const CATEGORY_ORDER: LocationCategoryOption[] = [...LOCATION_CATEGORY_OPTIONS, EVENT_CATEGORY_OPTION];

function suggestionItem(row: NearbySuggestion, state: "suggested" | "removed" | "additional"): SuggestedLocationItem {
  return {
    key: row.id,
    state,
    name: row.displayName,
    icon: row.icon,
    // Suggestions the AI left uncategorized still group (and filter) as Other.
    categoryOption: categoryOptionFor(row.tagKind, row.aiCategory) ?? OTHER_OPTION,
    city: row.city?.trim() ?? "",
    timing: row.tagKind === "Event" ? row.excludeFromIndex.event?.date ?? "" : "",
    location: previewLocation(row),
    suggestion: row,
  };
}

const suggestedItems = computed<SuggestedLocationItem[]>(() => suggestions.value.filter(isInReviewList).map(row => suggestionItem(row, "suggested")));
const additionalItems = computed<SuggestedLocationItem[]>(() =>
  suggestions.value
    .filter(isAdditional)
    .sort((a, b) => (a.aiRank ?? a.order ?? 0) - (b.aiRank ?? b.order ?? 0))
    .map(row => suggestionItem(row, "additional"))
);

const removedItems = computed<SuggestedLocationItem[]>(() => suggestions.value.filter(row => row.isVisible && row.reviewStatus === "Rejected").map(row => suggestionItem(row, "removed")));

// Saved Locations (added by hand here, or accepted on an earlier visit) sit in the same groups, marked "Added".
const addedItems = computed<SuggestedLocationItem[]>(() =>
  savedLocations.value
    .filter(location => !location.deletedDate && location.tagKind !== "Person")
    .map(location => ({
      key: location.id,
      state: "added" as const,
      name: location.name,
      icon: location.icon,
      categoryOption: categoryOptionFor(location.tagKind ?? "Location", location.category) ?? OTHER_OPTION,
      city: location.city?.trim() ?? "",
      location,
      savedLocation: location,
    }))
);

const activeItems = computed<SuggestedLocationItem[]>(() => [...suggestedItems.value, ...addedItems.value]);
const filterableItems = computed<SuggestedLocationItem[]>(() => [...activeItems.value, ...additionalItems.value]);

// Filters ====================================================================
const selectedType = ref<string>("");
const selectedCity = ref<string>("");
const search = ref<string>("");
const isSearchOpen = ref<boolean>(false);
const searchInputRef = ref<HTMLInputElement | null>(null);
const isShowingRemoved = ref<boolean>(false);
const isShowingAdded = ref<boolean>(true);
const expandedCategories = ref<Record<string, boolean>>({});

const typeChips = computed<FilterChip[]>(() => {
  const present = new Set(filterableItems.value.map(item => item.categoryOption.value));
  return [
    { value: "", label: "All" },
    ...CATEGORY_ORDER.filter(option => present.has(option.value)).map(option => ({ value: option.value, label: option.value === "Event" ? "Events" : option.label, categoryOption: option })),
  ];
});

/** Distinct cities, biggest first. */
const cities = computed<{ name: string; count: number }[]>(() => {
  const counts = new Map<string, number>();
  for (const item of filterableItems.value) if (item.city) counts.set(item.city, (counts.get(item.city) ?? 0) + 1);
  return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
});

const cityChips = computed<FilterChip[]>(() => [{ value: "", label: "All cities" }, ...cities.value.map(city => ({ value: city.name, label: city.name, count: city.count }))]);

// A filter can point at something that is gone after a remove, an edit, or a refresh.
watch(typeChips, chips => {
  if (!chips.some(chip => chip.value === selectedType.value)) selectedType.value = "";
});
watch(cities, list => {
  if (list.length <= 1 || !list.some(city => city.name === selectedCity.value)) selectedCity.value = "";
});

async function toggleSearch() {
  if (isSearchOpen.value) return closeSearch();
  isSearchOpen.value = true;
  await nextTick();
  searchInputRef.value?.focus();
}

function closeSearch() {
  search.value = "";
  isSearchOpen.value = false;
}

function filterItems(items: SuggestedLocationItem[]): SuggestedLocationItem[] {
  return items.filter(item => {
    if (selectedType.value && item.categoryOption.value !== selectedType.value) return false;
    if (selectedCity.value && item.city !== selectedCity.value) return false;
    const socialSet = item.location.socialSet;
    return locationTextMatches([item.name, item.city, socialSet?.instagramHandle, socialSet?.facebookPageName, socialSet?.tikTokHandle], search.value);
  });
}
// Hiding the already-added rows only takes them out of the list; they still count as included.
const filteredItems = computed<SuggestedLocationItem[]>(() => filterItems(isShowingAdded.value ? activeItems.value : suggestedItems.value));
const filteredAdditionalItems = computed<SuggestedLocationItem[]>(() => filterItems(additionalItems.value));

function hasSocialHandles(item: SuggestedLocationItem): boolean {
  const socialSet = item.location.socialSet;
  return Boolean(socialSet?.instagramHandle || socialSet?.facebookPageName || socialSet?.tikTokHandle);
}

// Within a group, the suggestions still up for review come first and the already-added Locations sit below them
// (those are settled, so they shouldn't push the rows needing a decision down the list). Inside each of those,
// rows with social accounts come first (they are the ones posts can tag); the sort is stable, so every block
// keeps its existing order.
function compareGroupItems(a: SuggestedLocationItem, b: SuggestedLocationItem): number {
  return Number(a.state === "added") - Number(b.state === "added") || Number(hasSocialHandles(b)) - Number(hasSocialHandles(a));
}

const groups = computed<{ option: LocationCategoryOption; includedCount: number; items: SuggestedLocationItem[]; additional: SuggestedLocationItem[] }[]>(() =>
  CATEGORY_ORDER.map(option => ({
    option,
    includedCount: activeItems.value.filter(item => item.categoryOption.value === option.value).length,
    items: filteredItems.value.filter(item => item.categoryOption.value === option.value).sort(compareGroupItems),
    additional: filteredAdditionalItems.value.filter(item => item.categoryOption.value === option.value),
  })).filter(group => group.items.length || group.additional.length)
);

function categoryLabel(option: LocationCategoryOption): string {
  return option.value === "Event" ? "Events" : option.label;
}

function additionalLabel(option: LocationCategoryOption, count: number): string {
  return `${count} more ${categoryLabel(option).toLowerCase()} ${count === 1 ? "option" : "options"}`;
}

const summaryText = computed<string>(() => `${activeItems.value.length} included · ${filteredAdditionalItems.value.length} more not included`);

const footerCountText = computed<string>(() => `${activeItems.value.length} included`);

// Edit / remove / restore ====================================================
const isSuggestionEditorVisible = ref<boolean>(false);
const suggestionToEdit = ref<NearbySuggestion | null>(null);
const isLocationEditorVisible = ref<boolean>(false);
const locationToEdit = ref<LocationData | null>(null);

function openLocationEditor(location: LocationData | null) {
  locationToEdit.value = location;
  isLocationEditorVisible.value = true;
}

function editItem(item: SuggestedLocationItem) {
  if (item.savedLocation) return openLocationEditor(item.savedLocation);
  if (!item.suggestion) return;
  suggestionToEdit.value = item.suggestion;
  isSuggestionEditorVisible.value = true;
}

function replaceSuggestion(saved: NearbySuggestion) {
  suggestions.value = suggestions.value.map(row => (row.id === saved.id ? saved : row));
  importFailures.value = importFailures.value.filter(failure => failure.suggestionId !== saved.id);
}

async function setReviewStatus(item: SuggestedLocationItem, reviewStatus: NearbyReviewStatus) {
  const row = item.suggestion;
  if (reviewStatus === "Pending" && row?.recommendationTier === "Additional") reviewStatus = "Approved";
  if (!row || isBusy.value) return;
  isBusy.value = true;
  loadError.value = "";
  try {
    replaceSuggestion(await NearbyLocationsRepo.update(customerId.value, row.id, { eTag: row._etag, reviewStatus }));
  } catch (error) {
    loadError.value = nearbyErrorMessage(error);
    // Most likely a stale row (edited elsewhere); pick up the current state.
    await loadSuggestions();
  } finally {
    isBusy.value = false;
  }
}

async function addAdditional(items: SuggestedLocationItem[]) {
  if (isBusy.value || isDiscoveryRunning.value) return;
  isBusy.value = true;
  loadError.value = "";
  try {
    const rows = items.map(item => item.suggestion).filter((row): row is NearbySuggestion => Boolean(row && isAdditional(row)));
    const result = await addSuggestionsToReview(customerId.value, rows);
    result.saved.forEach(replaceSuggestion);
    if (result.failures.length) {
      await loadSuggestions();
      loadError.value = `${result.failures.length} places couldn't be added to your list. ${result.failures[0].message}`;
    }
  } finally {
    isBusy.value = false;
  }
}

// Accept all =================================================================
async function goToNextStep() {
  // TODO: point at the new onboarding upload step once it exists.
  await router.push({ name: "CalendarUploadContent", params: { customerId: customerId.value } });
}

async function acceptAll() {
  if (isBusy.value) return;
  const rows = suggestedItems.value.map(item => item.suggestion).filter((row): row is NearbySuggestion => Boolean(row));
  if (!rows.length) return goToNextStep();

  isBusy.value = true;
  isAccepting.value = true;
  loadError.value = "";
  importFailures.value = [];
  try {
    // Events are added to the calendar the customer is working in.
    const calendarId = rows.some(row => row.tagKind === "Event") ? await getCalendarId(customerId.value) : null;
    const results = await importSuggestions(customerId.value, calendarId || null, rows);
    const failures = failedImports(results);
    MetricSender.sendMetric("Cust-AtlasOnboarding-Locations-AcceptAll", { customerId: customerId.value, accepted: results.length - failures.length, failed: failures.length });

    if (!failures.length) return await goToNextStep();

    importFailures.value = failures.map(failure => ({
      suggestionId: failure.suggestionId,
      name: rows.find(row => row.id === failure.suggestionId)?.displayName ?? "Suggestion",
      message: failure.message,
    }));
    await Promise.all([loadSuggestions(), loadSavedLocations()]);
  } catch (error) {
    getGlobalRemoteLogger()?.error(`SuggestedLocationsPage.acceptAll failed. customerId=${customerId.value} error=${error}`);
    loadError.value = nearbyErrorMessage(error);
    await Promise.all([loadSuggestions(), loadSavedLocations()]);
  } finally {
    isBusy.value = false;
    isAccepting.value = false;
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// Suggested locations page ===================================================
.suggested-locations-page {
  &__more-toggle {
    width: 100%;
    padding: 18px;
    border: 0;
    background: transparent;
    color: #102f41;
    font: 500 13px/18px $font-family;
    display: block;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: -3px;
      border-radius: 3px;
    }
  }
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__title {
    margin: 0;
  }

  &__subtitle {
    margin: 7px 0 0;
    color: #667085;
  }

  &__steps {
    margin: 22px 0 24px;
  }

  &__loader {
    margin: 40px 0;
    display: flex;
    justify-content: center;
  }

  &__section-heading {
    margin-bottom: 16px;
    padding-top: 22px;
    gap: 16px;
    border-top: 1px solid #e4e7ec;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__section-title {
    margin: 0;
  }

  &__section-subtitle {
    margin: 5px 0 0;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__note {
    margin-bottom: 16px;
  }

  &__failures {
    margin: 6px 0;
    padding-left: 20px;
  }

  &__filters {
    gap: 10px;
    display: grid;
  }

  &__search {
    margin-left: auto;
    gap: 6px;
    display: inline-flex;
    align-items: center;

    input {
      width: 220px;
      height: 34px;
      padding: 4px 10px;
      font-size: 14px;
    }
  }

  &__search-toggle {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    padding: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }

  &__summary {
    min-height: 34px;
    margin: 12px 0 8px;
    padding-left: 16px;
    gap: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #667085;
    font: 400 13px/18px sans-serif;
  }

  &__group {
    margin-bottom: 16px;
    border: 1px solid #e4e7ec;
    border-radius: 9px;
    overflow: hidden;

    &--removed {
      margin-top: 10px;

      > :first-child {
        border-top: 0;
      }
    }
  }

  &__group-header {
    padding: 18px 18px;
    gap: 9px;
    display: flex;
    align-items: center;
    background: #fff;
  }

  &__group-title {
    margin: 0;
    color: #102f41;
    font: 700 20px/28px $font-family;
  }

  &__group-count {
    color: #667085;
    font: 400 13px/18px sans-serif;
  }

  &__additional {
    padding: 0 18px;
    border-top: 1px solid #d8dfea;
    background: #f6f7fb;
  }

  &__additional-heading {
    position: relative;
    margin: 0 -18px;

    .suggested-locations-page__more-toggle {
      padding-right: 140px;
    }
  }

  &__additional-label {
    font: 500 14px/20px $font-family;
    display: inline-flex;
    align-items: center;
    gap: 9px;
  }

  &__include-all {
    position: absolute;
    right: 18px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }

  &__additional-hint {
    display: block;
    margin: 5px 0 0 25px;
    color: #667085;
    font: 400 12px/18px $font-family;

    strong {
      color: #102f41;
      font-weight: 500;
    }
  }

  &__chevron {
    flex: 0 0 7px;
    width: 7px;
    height: 7px;
    margin: 0 7px 3px 2px;
    border-right: 1px solid #667085;
    border-bottom: 1px solid #667085;
    transform: rotate(45deg);

    &--expanded {
      transform: rotate(225deg);
      margin-bottom: -3px;
    }
  }

  &__additional-rows {
    margin-left: 25px;
  }

  &__empty {
    padding: 28px;
    border: 1px dashed #d8dce2;
    border-radius: 9px;
    background: #f7f9fa;
    color: #667085;
    text-align: center;
  }

  &__removed {
    margin: 4px 0 16px;
  }

  // Stays in view while the list scrolls so the next step is always one click away.
  &__footer {
    margin-top: 20px;
    padding: 14px 18px;
    gap: 16px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    position: sticky;
    bottom: 0;
    z-index: 2;
    // A raised card with its own borders: the list scrolls underneath, so a bare top border made the list's
    // side borders look cut off.
    border: 1px solid #d8dce2;
    border-radius: 10px;
    background: #fff;

    // Pinned to the window edge: sit flush against it (no bottom border or corners) and lift off the list.
    &--stuck {
      border-bottom-color: transparent;
      border-radius: 10px 10px 0 0;
      box-shadow: 0 -6px 16px -6px rgba(16, 24, 40, 0.14);
    }
  }

  &__footer-count {
    color: #102f41;
    font: 600 14px/18px $font-family;
  }

  &__footer-hint {
    margin-top: 3px;
    color: #667085;
    font: 400 13px/18px sans-serif;
  }

  &__footer-next {
    margin-left: auto;
    color: #667085;
    font: 400 14px/18px sans-serif;

    strong {
      color: #102f41;
    }
  }
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .suggested-locations-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .suggested-locations-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .suggested-locations-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .suggested-locations-page {
    width: $tablet-content-width;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .suggested-locations-page {
    width: $mobile-content-width;

    &__group-header {
      padding: 16px 12px;
      flex-wrap: wrap;
    }

    &__additional {
      padding: 0 12px;
    }

    &__additional-heading {
      margin: 0 -12px;
    }

    &__more-toggle {
      padding: 14px 12px;
    }

    &__include-all {
      right: 12px;
    }

    &__additional-rows {
      margin-left: 8px;
    }

    &__summary {
      padding-left: 0;
      flex-wrap: wrap;
    }

    &__search--open {
      width: 100%;
      flex-wrap: wrap;
    }

    &__footer-next {
      display: none;
    }

    &__footer {
      padding: 12px;
      gap: 10px;
    }

    &__footer-status {
      flex-basis: 100%;
    }

    &__search input {
      width: 150px;
    }
  }
}
</style>
